import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Select } from '@tempo-io/sui-select';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemType, ItemTypeLevel, ItemTypeMap } from '@/common/api/types/itemType';
import { ItemTypeIcon } from '@/common/components/ui/item-type/itemTypeIcon';
import { useTableCellMutation } from '../hooks/useTableCellMutation';
import type { ItemsTableRow } from '../types';

type ItemTypeOption = {
  value: number;
  label: string;
  itemType: ItemType;
};

export const ItemTypeEditCell = ({
  itemTypesById,
  itemTypeLevelsById,
  id,
  row,
  field,
  api,
}: GridRenderEditCellParams<ItemsTableRow> & {
  itemTypesById: ItemTypeMap;
  itemTypeLevelsById: Record<number, ItemTypeLevel>;
}) => {
  const cellId = `item-type-${id}-${field}`;
  const { t } = useTranslation();
  const itemId = Number(id);

  const currentItemType = row.itemTypeId ? itemTypesById[row.itemTypeId] : undefined;
  const currentLevel = currentItemType
    ? itemTypeLevelsById[currentItemType.itemLevelId]
    : undefined;

  const options = React.useMemo<ItemTypeOption[]>(() => {
    if (!currentLevel) return [];
    return Object.values(itemTypesById)
      .filter((type) => {
        const level = itemTypeLevelsById[type.itemLevelId];
        return level && level.level === currentLevel.level;
      })
      .map((type) => ({ value: type.id, label: type.name, itemType: type }));
  }, [itemTypesById, itemTypeLevelsById, currentLevel]);

  const { commit, isPending } = useTableCellMutation();

  const selectedOption = React.useMemo(
    () => options.find((opt) => opt.value === row.itemTypeId) ?? null,
    [options, row.itemTypeId],
  );

  return (
    <Select<ItemTypeOption>
      id={cellId}
      name={cellId}
      cellEdit
      autoFocus
      openMenuOnFocus
      value={selectedOption}
      onChange={(opt) => {
        if (!opt) return;
        commit({
          itemId,
          api,
          row,
          data: { itemTypeId: opt.value },
        });
      }}
      options={options}
      formatOptionLabel={(option) => <OptionWithIcon option={option} />}
      getOptionValue={(option) => String(option.value)}
      getOptionLabel={(option) => option.label}
      placeholder={t(($) => $.adaptivePlanner.itemPanel.selectPlaceholders.type)}
      isDisabled={isPending}
    />
  );
};

const OptionWithIcon = ({ option }: { option: ItemTypeOption }) => (
  <div className="flex items-center gap-2 min-w-0">
    <ItemTypeIcon itemType={option.itemType} className="w-4 h-4 shrink-0" aria-hidden />
    <span className="truncate">{option.label}</span>
  </div>
);
